import { truncateDecimals } from './markerUtils';
import * as turf from '@turf/turf';

/**
 * @function createR0TextOverlay
 * @description Builds a Google Maps custom OverlayView class that renders a plain text label
 * ("R0") anchored to a map position, supporting visibility/opacity toggling.
 * **Called by:
 * @see {drawAngleAndR0}
 * @memberof module:Mepbay
 * @returns {Function} The `R0TextOverlay` class (extends google.maps.OverlayView).
 */
const createR0TextOverlay = () => {
  return class R0TextOverlay extends window.google.maps.OverlayView {
    constructor(position, text, map) {
      super();
      this.position = position;
      this.text = text;
      this.div = null;
      this.setMap(map);
    }

    onAdd() {
      const div = document.createElement('div');
      div.style.position = 'absolute';
      div.style.color = 'black';
      div.style.fontSize = '12px';
      div.style.fontWeight = 'bold';
      div.style.padding = '2px 4px';
      div.style.whiteSpace = 'nowrap';
      div.style.pointerEvents = 'none';
      div.innerHTML = this.text;

      this.div = div;
      const panes = this.getPanes();
      panes.overlayMouseTarget.appendChild(div);
    }

    draw() {
      const overlayProjection = this.getProjection();
      const position = overlayProjection.fromLatLngToDivPixel(this.position);

      if (this.div) {
        this.div.style.left = position.x + 10 + 'px';
        this.div.style.top = position.y - 10 + 'px';
      }
    }

    onRemove() {
      if (this.div) {
        this.div.parentNode.removeChild(this.div);
        this.div = null;
      }
    }

    setVisible(visible) {
      if (this.div) {
        this.div.style.display = visible ? 'block' : 'none';
      }
    }

    setOpacity(opacity) {
      if (this.div) {
        this.div.style.opacity = opacity;
      }
    }
  };
};

/**
 * @function createAngleArc
 * @description Creates an arc representing the angle between two bearings using Turf.js.
 * @param {Object} center - Center point {lat, lng}
 * @param {Number} startBearing - Start bearing in degrees
 * @param {Number} endBearing - End bearing in degrees
 * @param {Number} radius - Radius of the arc in meters
 * @param {Number} [segments=20] - Number of segments to create the arc
 * **Called by:
 * @see {drawAngleAndR0}
 * @memberof module:Mepbay
 * @returns {Array<{lat: Number, lng: Number}>} Array of points representing the arc
 */
const createAngleArc = (center, startBearing, endBearing, radius, segments = 20) => {
  const points = [];

  let deltaAngle = endBearing - startBearing;

  if (deltaAngle > 180) deltaAngle -= 360;
  if (deltaAngle < -180) deltaAngle += 360;

  const step = deltaAngle / segments;
  let currentBearing = startBearing;

  for (let i = 0; i <= segments; i++) {
    const point = turf.destination([center.lng, center.lat], radius / 1000, currentBearing, { units: 'kilometers' });

    const coords = point.geometry.coordinates;
    points.push({ lat: coords[1], lng: coords[0] });

    currentBearing += step;
  }

  return points;
};

/**
 * @function drawAngleAndR0
 * @description Draws the beta angle arc between the closure and auxiliary points (as seen from
 * the diffraction point), labels it with the beta angle value, and draws the R0 text overlay at
 * the midpoint between the diffraction and closure points.
 * 1. Computes the arc radius from the shorter of the diffraction-to-closure and
 *    diffraction-to-auxiliary distances, and the bearings to each point.
 * 2. Orders the start/end bearings so the arc sweeps the correct direction based on the sign and
 *    magnitude of beta.
 * 3. Draws the arc as a filled polygon and places a beta-angle label marker beyond its midpoint.
 * 4. Replaces any previous R0 text overlay with a new one at the diffraction/closure midpoint.
 * @param {Object} params - Parameters object
 * @param {Object} params.layer - The layer object to add visualizations to
 * @param {Object} params.mapRef - Reference to the Google Maps instance
 * @param {Object} params.diffPoint - Diffraction point {lat, lng}
 * @param {Object} params.clsPoint - Closure point {lat, lng}
 * @param {Object} params.auxPoint - Auxiliary point {lat, lng}
 * @param {Number} params.beta - Beta angle, in degrees
 * @see {@link createAngleArc}
 * @see {@link createR0TextOverlay}
 * @see {@link truncateDecimals}
 * **Called by:
 * @see {ProcessingPage} - MEPBay module's processing page (imported as ProcessingMepBayPage).
 * @memberof module:Mepbay
 * @returns {void}
 */
export const drawAngleAndR0 = ({ layer, mapRef, diffPoint, clsPoint, auxPoint, beta }) => {
  const diffTurf = turf.point([diffPoint.lng, diffPoint.lat]);
  const clsTurf = turf.point([clsPoint.lng, clsPoint.lat]);
  const auxTurf = turf.point([auxPoint.lng, auxPoint.lat]);

  const distDiffToCls = turf.distance(diffTurf, clsTurf, { units: 'meters' });
  const distDiffToAux = turf.distance(diffTurf, auxTurf, { units: 'meters' });

  const bearingToCls = turf.bearing(diffTurf, clsTurf);
  const bearingToAux = turf.bearing(diffTurf, auxTurf);

  const arcRadius = Math.min(distDiffToCls, distDiffToAux) * 0.3;

  let startBearing = bearingToCls;
  let endBearing = bearingToAux;

  if (beta < 0) {
    [startBearing, endBearing] = [endBearing, startBearing];
  }

  if (beta > 180) {
    [startBearing, endBearing] = [endBearing, startBearing];
  }

  const arcPointsCA = createAngleArc(diffPoint, startBearing, endBearing, arcRadius);

  const arcPathCA = [
    { lat: diffPoint.lat, lng: diffPoint.lng },
    ...arcPointsCA,
    { lat: diffPoint.lat, lng: diffPoint.lng },
  ];

  const arcCA = new window.google.maps.Polygon({
    paths: arcPathCA,
    strokeColor: layer.color,
    strokeOpacity: layer.opacity * 0.8,
    strokeWeight: 2,
    fillColor: layer.color,
    fillOpacity: Math.min(layer.opacity, 0.1),
    visible: layer.visible,
  });
  arcCA.setMap(mapRef.current);
  layer.lines.push(arcCA);

  const midBearing = (startBearing + endBearing) / 2;
  const textRadiusCA = arcRadius * 1.5;

  const textPosTurf = turf.destination([diffPoint.lng, diffPoint.lat], textRadiusCA / 1000, midBearing, {
    units: 'kilometers',
  });

  const textCoords = textPosTurf.geometry.coordinates;

  const angleCAMarker = new window.google.maps.Marker({
    position: { lat: textCoords[1], lng: textCoords[0] },
    map: mapRef.current,
    title: `Ângulo Beta: ${truncateDecimals(beta, 2)}°`,
    icon: {
      url:
        'data:image/svg+xml;charset=UTF-8,' +
        encodeURIComponent(`
        <svg width="60" height="20" viewBox="0 0 60 20" xmlns="http://www.w3.org/2000/svg">
          <text x="30" y="14" text-anchor="middle" color="black" fill="black" font-size="10" font-weight="bold" opacity="${layer.opacity}">${truncateDecimals(beta, 2)}°</text>
        </svg>
      `),
      scaledSize: new window.google.maps.Size(60, 20),
      anchor: new window.google.maps.Point(30, 10),
    },
  });
  layer.markers['angleCA'] = angleCAMarker;
  const midPoint = {
    lat: (diffPoint.lat + clsPoint.lat) / 2,
    lng: (diffPoint.lng + clsPoint.lng) / 2,
  };

  if (layer.r0Overlay) {
    layer.r0Overlay.setMap(null);
  }

  const r0Text = `R0`;
  const R0TextOverlay = createR0TextOverlay();
  const r0Overlay = new R0TextOverlay(midPoint, r0Text, mapRef.current);
  layer.r0Overlay = r0Overlay;
};
